import React from "react";
import { Link } from "react-router-dom";
import { Shirt, Gauge, Factory, ArrowRight, Clapperboard } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brand";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const buildClips = (inverterUrl) => [
  {
    slug: "fabric-test",
    icon: Shirt,
    title: "Fabric pressing test",
    caption: "Heavy cotton and native fabric pressed smooth with the 500W CORE 500 — watch creases lift on a single pass with the thermostat on the cotton setting.",
    src: BRAND_ASSETS.fabricTestVideo,
    poster: BRAND_ASSETS.fabricTestPoster,
  },
  {
    slug: "inverter-test",
    icon: Gauge,
    title: "Real-world inverter load test",
    caption: "The CORE 500 plugged into a household inverter with a power meter on the line — the reading settles at ~500W, where a conventional iron would show 1,500W–2,400W.",
    src: inverterUrl || BRAND_ASSETS.inverterTestVideo,
    poster: BRAND_ASSETS.inverterTestPoster,
    placeholder: "500W meter-reading video coming soon!",
  },
  {
    slug: "factory-qa",
    icon: Factory,
    title: "Factory manufacturing & QA",
    caption: "Behind the scenes on our line: hand assembly, then every unit heat-cycled and electrically checked on the 12-station quality rig before it is boxed.",
    src: BRAND_ASSETS.factoryQaVideo,
    poster: BRAND_ASSETS.factoryQaPoster,
  },
];

const ClipPlaceholder = ({ c }) => (
  <div className="absolute inset-0 flex items-center justify-center text-center p-6" data-testid={`action-placeholder-${c.slug}`}>
    {c.poster && <img src={c.poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />}
    <div className="absolute inset-0" style={{ background: "rgba(31,26,20,0.55)" }} />
    <div className="relative z-10 inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-sm font-medium shadow-xl" style={{ background: "#FCF6EA", color: "var(--ink)", border: "1px solid var(--sun-orange)" }}>
      <Clapperboard size={16} style={{ color: "var(--sun-orange)" }} />
      {c.placeholder || "Video coming soon!"}
    </div>
  </div>
);

const ClipCard = ({ c }) => {
  const I = c.icon;
  return (
    <figure data-testid={`action-clip-${c.slug}`} className="flex flex-col">
      <div className="relative overflow-hidden glossy-deep rounded-2xl" style={{ aspectRatio: "16/10", background: "#1F1A14" }}>
        {c.src ? (
          <video
            data-testid={`action-video-${c.slug}`}
            src={c.src}
            poster={c.poster || undefined}
            controls
            playsInline
            preload="none"
            className="w-full h-full object-cover"
          />
        ) : (
          <ClipPlaceholder c={c} />
        )}
      </div>
      <figcaption className="mt-4 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0" style={{ background: "var(--brand-tint)" }}>
          <I size={16} style={{ color: "var(--sun-orange)" }} strokeWidth={2} />
        </span>
        <span>
          <span className="block font-serif text-xl" style={{ color: "var(--ink)" }}>{c.title}</span>
          <span className="block text-sm mt-1.5 leading-relaxed" style={{ color: "var(--ink-soft)" }} data-testid={`action-caption-${c.slug}`}>{c.caption}</span>
        </span>
      </figcaption>
    </figure>
  );
};

export default function SeeItInActionSection() {
  const siteConfig = useSiteConfig();
  const clips = buildClips(siteConfig?.inverter_video_url);
  return (
    <section
      id="see-it-in-action"
      data-testid="see-it-in-action"
      className="py-20 md:py-28"
      style={{ background: "var(--surface-2)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", scrollMarginTop: "140px" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow">See it in action</div>
            <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
              Real fabrics. Real inverters. <span className="sunset-text">Real results.</span>
            </h2>
          </div>
          <div className="md:col-span-5 text-base" style={{ color: "var(--ink-soft)" }}>
            No pop-ups, no gated clips — every demo plays right here. Captions explain what you are
            seeing, so it makes sense even on mute.
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-10 md:gap-8">
          {clips.map((c) => <ClipCard key={c.slug} c={c} />)}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row flex-wrap gap-3">
          <Link to="/videos" data-testid="action-all-videos" className="btn-outline justify-center">
            Watch all demo videos <ArrowRight size={16} />
          </Link>
          <Link to="/production" data-testid="action-production-album" className="inline-flex items-center justify-center gap-2 text-sm font-medium sunset-text py-2 px-2">
            View the full production album <ArrowRight size={14} color="var(--sun-orange)" />
          </Link>
        </div>
      </div>
    </section>
  );
}
